import { HorseRead, HorseSearchResponse, HorseFilters, UserRead, Token, UserHorseCollectionRead, UserHorseNoteRead, UserHorseNoteCreate, UserHorseNoteUpdate, MediaFileRead, MediaFileUploadResponse, MediaFileListResponse, MediaLinkCreate, MediaLinkResponse, PhoneVerificationRequest, PhoneVerificationResponse, VerifyCodeRequest, VerifyCodeResponse, DetachPhoneResponse, HorsePricingRead, HorsePricingCreate, HorsePricingUpdate, HorsePropertiesRead, HorsePropertiesCreate, HorsePropertiesUpdate, MediaUsageResponse, ContactRead, ContactCreate, ContactUpdate, PrivateHorseCreateRequest, PrivateHorseLinkRequest, CompetitionRead, CompetitionClassRead, CompetitionSearchFilters, CompetitionSearchResponse, CompetitionResultRead, EventDetailRead, Country, ProfileRead, ProfileWithHorseCount, ProfileCreate, ProfileUpdate, VapidPublicKeyResponse, DeviceSubscriptionRequest, DeviceUnsubscribeRequest, DeviceRead, InboxMessage, InboxMessageList, InboxUnreadCount } from '../types/api';

// TEMPORARY: Replace with your backend ngrok URL for Safari testing
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
// For ngrok testing, use: const API_BASE_URL = 'https://YOUR-BACKEND-NGROK-URL.ngrok.io';

class ApiService {
  private isNgrokUrl(): boolean {
    return API_BASE_URL.includes('ngrok');
  }

  private getCommonHeaders(): HeadersInit {
    const headers: HeadersInit = {};
    
    // Only add ngrok header when using ngrok
    if (this.isNgrokUrl()) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }
    
    return headers;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token');
    if (!token) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      throw new Error('No authentication token available. Please log in.');
    }
    
    const headers: HeadersInit = {
      'Authorization': `Bearer ${token}`,
    };
    
    // Only add ngrok header when using ngrok
    if (this.isNgrokUrl()) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }
    
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        this.logout();
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        throw new Error('Session expired. Please log in again.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async login(username: string, password: string): Promise<Token> {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        ...this.getCommonHeaders(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    });

    const token = await this.handleResponse<Token>(response);
    try {
      localStorage.setItem('access_token', token.access_token);
    } catch (error) {
      console.error('Failed to save token to localStorage. Safari privacy settings may be blocking storage.', error);
      throw new Error('Unable to save login session. Please disable "Prevent Cross-Site Tracking" in Safari settings.');
    }
    return token;
  }

  async getCurrentUser(): Promise<UserRead> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<UserRead>(response);
  }

  async getCountries(): Promise<Country[]> {
    const response = await fetch(`${API_BASE_URL}/countries`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<Country[]>(response);
  }

  async searchHorses(filters: HorseFilters = {}, skip = 0, limit = 100): Promise<HorseSearchResponse> {
    const params = new URLSearchParams();

    // Name filter
    if (filters.name) params.append('name', filters.name);
    
    // Sex filter - backend supports single sex only
    if (filters.sex && filters.sex.length === 1) params.append('sex', filters.sex[0]);
    
    // Birth year filter - support both exact year and range
    if (
      filters.birth_year_min !== undefined &&
      filters.birth_year_max !== undefined &&
      filters.birth_year_min === filters.birth_year_max
    ) {
      // If min=max, send as exact year for backward compatibility
      params.append('year', filters.birth_year_min.toString());
    } else {
      // Send as range
      if (filters.birth_year_min !== undefined) {
        params.append('birth_year_min', filters.birth_year_min.toString());
      }
      if (filters.birth_year_max !== undefined) {
        params.append('birth_year_max', filters.birth_year_max.toString());
      }
    }
    
    // Show level filter (cm)
    if (filters.show_level_min !== undefined) {
      params.append('show_level_min', filters.show_level_min.toString());
    }
    if (filters.show_level_max !== undefined) {
      params.append('show_level_max', filters.show_level_max.toString());
    }
    
    // Breed/studbook filter
    if (filters.breed) params.append('breed', filters.breed);
    
    // Sire filter
    if (filters.sire) params.append('sire', filters.sire);
    
    // Dam filter
    if (filters.dam) params.append('dam', filters.dam);
    
    // Collection filter
    if (filters.in_collection !== undefined) params.append('in_collection', filters.in_collection.toString());

    // Competition count filters
    if (filters.min_competitions !== undefined) params.append('min_competitions', filters.min_competitions.toString());
    if (filters.max_competitions !== undefined) params.append('max_competitions', filters.max_competitions.toString());

    // Prize filters per currency
    if (filters.min_prize_eur !== undefined) params.append('min_prize_eur', filters.min_prize_eur.toString());
    if (filters.min_prize_gbp !== undefined) params.append('min_prize_gbp', filters.min_prize_gbp.toString());
    if (filters.min_prize_usd !== undefined) params.append('min_prize_usd', filters.min_prize_usd.toString());
    if (filters.min_prize_chf !== undefined) params.append('min_prize_chf', filters.min_prize_chf.toString());
    if (filters.min_prize_aud !== undefined) params.append('min_prize_aud', filters.min_prize_aud.toString());
    if (filters.min_prize_cad !== undefined) params.append('min_prize_cad', filters.min_prize_cad.toString());

    // Physical height filters
    if (filters.height_cm_min !== undefined) params.append('height_cm_min', filters.height_cm_min.toString());
    if (filters.height_cm_max !== undefined) params.append('height_cm_max', filters.height_cm_max.toString());

    // Pony filter
    if (filters.is_pony !== undefined) params.append('is_pony', filters.is_pony.toString());

    // Has videos filter
    if (filters.has_videos !== undefined) params.append('has_videos', filters.has_videos.toString());

    // Competition recency filter
    if (filters.last_result_within !== undefined && filters.last_result_unit) {
      params.append('last_result_within', filters.last_result_within.toString());
      params.append('last_result_unit', filters.last_result_unit);
    }

    // Country filter — send each code as a repeated ?countries= param
    if (filters.countries?.length) {
      filters.countries.forEach(code => params.append('countries', code));
    }

    // Pagination
    params.append('skip', skip.toString());
    params.append('limit', limit.toString());

    try {
      const headers = this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/horses?${params}`, {
        headers,
      });
      
      return this.handleResponse<HorseSearchResponse>(response);
    } catch (error) {
      if (error instanceof Error && error.message.includes('authentication')) {
        // Clear invalid token and throw error
        this.logout();
        throw new Error('Authentication required. Please log in again.');
      }
      throw error;
    }
  }

  async getHorseDetails(horseId: string): Promise<HorseRead> {
    const response = await fetch(`${API_BASE_URL}/horses/${horseId}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<HorseRead>(response);
  }

  async getHorseByFeiId(feiId: string): Promise<HorseRead> {
    const response = await fetch(`${API_BASE_URL}/horses/fei/${feiId}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<HorseRead>(response);
  }

  async getHorseCompetitions(horseId: string, skip: number = 0, limit: number = 100): Promise<CompetitionRead[]> {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    const response = await fetch(`${API_BASE_URL}/horses/${horseId}/competitions?${params}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<CompetitionRead[]>(response);
  }

  async addToCollection(horseId: string, status: string = "own"): Promise<UserHorseCollectionRead> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        notification_enabled: true,
        status
      }),
    });
    return this.handleResponse<UserHorseCollectionRead>(response);
  }

  async removeFromCollection(horseId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.logout();
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        throw new Error('Session expired. Please log in again.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to remove horse from collection: ${response.status}`);
    }
  }

  async updateCollectionStatus(horseId: string, status: string, notificationEnabled?: boolean): Promise<UserHorseCollectionRead> {
    const updateData: any = { status };
    if (notificationEnabled !== undefined) {
      updateData.notification_enabled = notificationEnabled;
    }

    const response = await fetch(`${API_BASE_URL}/collection/${horseId}`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });
    return this.handleResponse<UserHorseCollectionRead>(response);
  }

  async getCollection(): Promise<UserHorseCollectionRead[]> {
    const response = await fetch(`${API_BASE_URL}/collection?include_skipped=true`, {
      headers: this.getAuthHeaders(),
    });
    
    // Check for warnings about missing horse data
    const warningHeader = response.headers.get('X-Collection-Warning');
    const missingCount = response.headers.get('X-Missing-Horses-Count');
    const missingIds = response.headers.get('X-Missing-Horses-Ids');
    
    if (warningHeader && missingCount) {
      console.warn(
        `⚠️ Collection Warning: ${missingCount} horse(s) in your collection have missing data.`,
        missingIds ? `Horse IDs: ${missingIds}` : ''
      );
      console.warn('These horses may have been deleted from the source database or are not yet synced.');
      console.warn('Consider removing them from your collection or contacting support.');
    }
    
    return this.handleResponse<UserHorseCollectionRead[]>(response);
  }

  async getCollectionItem(horseId: string): Promise<UserHorseCollectionRead | null> {
    const collection = await this.getCollection();
    return collection.find(item => item.horse_id === horseId) || null;
  }

  async updatePrivateHorseName(horseId: string, privateName: string | null): Promise<UserHorseCollectionRead> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/private-name`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ private_name: privateName }),
    });
    return this.handleResponse<UserHorseCollectionRead>(response);
  }

  logout(): void {
    localStorage.removeItem('access_token');
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  // Horse Notes API methods
  async getHorseNotes(horseId: string, noteType?: string): Promise<UserHorseNoteRead[]> {
    const params = new URLSearchParams();
    if (noteType) params.append('note_type', noteType);
    
    const url = `${API_BASE_URL}/collection/${horseId}/notes${params.toString() ? `?${params}` : ''}`;
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<UserHorseNoteRead[]>(response);
  }

  async createHorseNote(horseId: string, noteData: UserHorseNoteCreate): Promise<UserHorseNoteRead> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/notes`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(noteData),
    });
    return this.handleResponse<UserHorseNoteRead>(response);
  }

  async updateHorseNote(horseId: string, noteId: string, noteData: UserHorseNoteUpdate): Promise<UserHorseNoteRead> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/notes/${noteId}`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(noteData),
    });
    return this.handleResponse<UserHorseNoteRead>(response);
  }

  async deleteHorseNote(horseId: string, noteId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/notes/${noteId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete note: ${response.status}`);
    }
  }

  // Contacts Management
  async listContacts(skip: number = 0, limit: number = 100): Promise<ContactRead[]> {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    const response = await fetch(`${API_BASE_URL}/contacts?${params}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<ContactRead[]>(response);
  }

  async createContact(contactData: ContactCreate): Promise<ContactRead> {
    const response = await fetch(`${API_BASE_URL}/contacts`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactData),
    });

    return this.handleResponse<ContactRead>(response);
  }

  async updateContact(contactId: string, contactData: ContactUpdate): Promise<ContactRead> {
    const response = await fetch(`${API_BASE_URL}/contacts/${contactId}`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactData),
    });

    return this.handleResponse<ContactRead>(response);
  }

  async deleteContact(contactId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/contacts/${contactId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete contact: ${response.status}`);
    }
  }

  // Media Management Methods
  async uploadMediaFile(file: File, notes?: string): Promise<MediaFileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (notes) {
      formData.append('notes', notes);
    }

    const response = await fetch(`${API_BASE_URL}/media/upload`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        // Don't set Content-Type for FormData - let the browser set it with boundary
      },
      body: formData,
    });

    return this.handleResponse<MediaFileUploadResponse>(response);
  }

  async listMediaFiles(page: number = 1, pageSize: number = 20): Promise<MediaFileListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    const response = await fetch(`${API_BASE_URL}/media?${params}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<MediaFileListResponse>(response);
  }

  async getMediaFileInfo(fileId: string): Promise<MediaFileRead> {
    const response = await fetch(`${API_BASE_URL}/media/${fileId}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<MediaFileRead>(response);
  }

  async updateMediaFileNotes(fileId: string, notes: string): Promise<MediaFileRead> {
    const response = await fetch(`${API_BASE_URL}/media/${fileId}/notes`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ notes }),
    });

    return this.handleResponse<MediaFileRead>(response);
  }

  async updateMediaFileUIFilename(fileId: string, ui_filename: string): Promise<MediaFileRead> {
    const response = await fetch(`${API_BASE_URL}/media/${fileId}/update-ui-filename`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ui_filename }),
    });

    return this.handleResponse<MediaFileRead>(response);
  }

  async deleteMediaFile(fileId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/media/${fileId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete media file: ${response.status}`);
    }
  }

  async getMediaUsage(fileId: string): Promise<MediaUsageResponse> {
    const response = await fetch(`${API_BASE_URL}/media/${fileId}/usage`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get media usage: ${response.status}`);
    }

    return response.json();
  }

  getMediaFileDownloadUrl(fileId: string): string {
    return `${API_BASE_URL}/media/download/${fileId}`;
  }

  async fetchMediaBlob(fileId: string): Promise<Blob> {
    const downloadUrl = this.getMediaFileDownloadUrl(fileId);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(downloadUrl, {
      method: 'GET',
      headers: headers,
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch media: ${response.statusText}`);
    }
    
    return response.blob();
  }

  // Media Linking Methods
  async linkMediaToHorse(horseId: string, linkData: MediaLinkCreate): Promise<MediaLinkResponse> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/link-media`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(linkData),
    });

    return this.handleResponse<MediaLinkResponse>(response);
  }

  async getLinkedMedia(horseId: string, skip: number = 0, limit: number = 20): Promise<MediaLinkResponse[]> {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });

    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/linked-media?${params}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<MediaLinkResponse[]>(response);
  }

  // Phone Verification Methods
  async requestPhoneVerification(phoneNumber: string): Promise<PhoneVerificationResponse> {
    const requestData: PhoneVerificationRequest = {
      phone_number: phoneNumber
    };

    const response = await fetch(`${API_BASE_URL}/auth/request-verification`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    return this.handleResponse<PhoneVerificationResponse>(response);
  }

  async verifyPhoneCode(phoneNumber: string, verificationCode: string): Promise<VerifyCodeResponse> {
    const requestData: VerifyCodeRequest = {
      phone_number: phoneNumber,
      verification_code: verificationCode
    };

    const response = await fetch(`${API_BASE_URL}/auth/verify-code`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    return this.handleResponse<VerifyCodeResponse>(response);
  }

  async detachPhone(): Promise<DetachPhoneResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/detach-phone`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<DetachPhoneResponse>(response);
  }

  // Horse Pricing Methods
  async getHorsePricing(horseId: string): Promise<HorsePricingRead | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/collection/${horseId}/pricing`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (response.status === 404) {
        return null;
      }

      return this.handleResponse<HorsePricingRead>(response);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async createHorsePricing(horseId: string, pricingData: HorsePricingCreate): Promise<HorsePricingRead> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/pricing`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(pricingData),
    });

    return this.handleResponse<HorsePricingRead>(response);
  }

  async updateHorsePricing(horseId: string, pricingData: HorsePricingUpdate): Promise<HorsePricingRead> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/pricing`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(pricingData),
    });

    return this.handleResponse<HorsePricingRead>(response);
  }

  async deleteHorsePricing(horseId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/pricing`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete pricing: ${response.status}`);
    }
  }

  // Horse Properties Methods
  async getHorseProperties(horseId: string): Promise<HorsePropertiesRead | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/collection/${horseId}/properties`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (response.status === 404) {
        return null;
      }

      return this.handleResponse<HorsePropertiesRead>(response);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async createHorseProperties(horseId: string, propertiesData: HorsePropertiesCreate): Promise<HorsePropertiesRead> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/properties`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(propertiesData),
    });

    return this.handleResponse<HorsePropertiesRead>(response);
  }

  async updateHorseProperties(horseId: string, propertiesData: HorsePropertiesUpdate): Promise<HorsePropertiesRead> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/properties`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(propertiesData),
    });

    return this.handleResponse<HorsePropertiesRead>(response);
  }

  async deleteHorseProperties(horseId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/properties`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete properties: ${response.status}`);
    }
  }

  async createPrivateHorse(payload: PrivateHorseCreateRequest): Promise<UserHorseCollectionRead> {
    const response = await fetch(`${API_BASE_URL}/private-horses`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    return this.handleResponse<UserHorseCollectionRead>(response);
  }

  async linkPrivateHorse(privateHorseId: string, linkRequest: PrivateHorseLinkRequest): Promise<UserHorseCollectionRead> {
    const response = await fetch(`${API_BASE_URL}/private-horses/${privateHorseId}/link`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(linkRequest),
    });
    return this.handleResponse<UserHorseCollectionRead>(response);
  }

  async deletePrivateHorse(privateHorseId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/private-horses/${privateHorseId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete private horse: ${response.status}`);
    }
  }

  // Profile Methods
  async getProfiles(): Promise<ProfileWithHorseCount[]> {
    const response = await fetch(`${API_BASE_URL}/profiles`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<ProfileWithHorseCount[]>(response);
  }

  async createProfile(data: ProfileCreate): Promise<ProfileRead> {
    const response = await fetch(`${API_BASE_URL}/profiles`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return this.handleResponse<ProfileRead>(response);
  }

  async updateProfile(profileId: string, data: ProfileUpdate): Promise<ProfileRead> {
    const response = await fetch(`${API_BASE_URL}/profiles/${profileId}`, {
      method: 'PATCH',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return this.handleResponse<ProfileRead>(response);
  }

  async deleteProfile(profileId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/profiles/${profileId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete profile: ${response.status}`);
    }
  }

  async assignProfilesToHorse(horseId: string, profileIds: string[]): Promise<UserHorseCollectionRead> {
    const response = await fetch(`${API_BASE_URL}/collection/${horseId}/profiles`, {
      method: 'PUT',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ profile_ids: profileIds }),
    });
    return this.handleResponse<UserHorseCollectionRead>(response);
  }

  // Competition Search Methods
  async searchCompetitions(filters: CompetitionSearchFilters = {}, skip = 0, limit = 100): Promise<CompetitionSearchResponse> {
    const params = new URLSearchParams();

    if (filters.name) params.append('name', filters.name);
    if (filters.competition_name) params.append('competition_name', filters.competition_name);
    if (filters.location) params.append('location', filters.location);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.height_cm_min !== undefined) params.append('height_cm_min', filters.height_cm_min.toString());
    if (filters.height_cm_max !== undefined) params.append('height_cm_max', filters.height_cm_max.toString());
    if (filters.is_pony_class !== undefined) params.append('is_pony_class', filters.is_pony_class.toString());

    params.append('skip', skip.toString());
    params.append('limit', limit.toString());

    const response = await fetch(`${API_BASE_URL}/competitions?${params}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<CompetitionSearchResponse>(response);
  }

  async getCompetitionDetails(classId: string): Promise<CompetitionClassRead> {
    const response = await fetch(`${API_BASE_URL}/competitions/${encodeURIComponent(classId)}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<CompetitionClassRead>(response);
  }

  async getCompetitionResults(classId: string, limit = 200): Promise<CompetitionResultRead[]> {
    const params = new URLSearchParams({ limit: limit.toString() });
    const response = await fetch(`${API_BASE_URL}/competitions/${encodeURIComponent(classId)}/results?${params}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<CompetitionResultRead[]>(response);
  }

  async getEventDetails(competitionId: string): Promise<EventDetailRead> {
    const response = await fetch(`${API_BASE_URL}/competitions/events/${encodeURIComponent(competitionId)}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<EventDetailRead>(response);
  }

  async getEventClasses(competitionId: string, limit = 200): Promise<CompetitionClassRead[]> {
    const params = new URLSearchParams({ limit: limit.toString() });
    const response = await fetch(`${API_BASE_URL}/competitions/events/${encodeURIComponent(competitionId)}/classes?${params}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<CompetitionClassRead[]>(response);
  }

  // Inbox Methods
  async getInboxMessages(skip = 0, limit = 20): Promise<InboxMessageList> {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    const response = await fetch(`${API_BASE_URL}/inbox?${params}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<InboxMessageList>(response);
  }

  async getInboxMessage(messageId: string): Promise<InboxMessage> {
    const response = await fetch(`${API_BASE_URL}/inbox/${encodeURIComponent(messageId)}`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<InboxMessage>(response);
  }

  async getInboxUnreadCount(): Promise<InboxUnreadCount> {
    const response = await fetch(`${API_BASE_URL}/inbox/unread-count`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<InboxUnreadCount>(response);
  }

  async markInboxRead(messageId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/inbox/${encodeURIComponent(messageId)}/read`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to mark inbox message as read: ${response.status}`);
    }
  }

  async markAllInboxRead(): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/inbox/read-all`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to mark all inbox messages as read: ${response.status}`);
    }
  }

  // Push Notification Methods

  async getVapidPublicKey(): Promise<VapidPublicKeyResponse> {
    const response = await fetch(`${API_BASE_URL}/notifications/vapid-public-key`, {
      headers: this.getCommonHeaders(),
    });
    return this.handleResponse<VapidPublicKeyResponse>(response);
  }

  async subscribePush(data: DeviceSubscriptionRequest): Promise<DeviceRead> {
    const response = await fetch(`${API_BASE_URL}/notifications/subscribe`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return this.handleResponse<DeviceRead>(response);
  }

  async unsubscribePush(data: DeviceUnsubscribeRequest): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notifications/unsubscribe`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`Failed to unsubscribe: ${response.status}`);
    }
  }

  async listNotificationDevices(): Promise<DeviceRead[]> {
    const response = await fetch(`${API_BASE_URL}/notifications/devices`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse<DeviceRead[]>(response);
  }

  async removeAllNotificationDevices(): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notifications/devices`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to remove devices: ${response.status}`);
    }
  }

  async reportNotificationReceived(messageId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/notifications/received`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ message_id: messageId }),
    });
    if (!response.ok) {
      throw new Error(`Failed to report receipt: ${response.status}`);
    }
  }
}

export const apiService = new ApiService(); 